import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Scale, RefreshCw, Search, MapPin, Building2, IndianRupee, 
  TrendingDown, TrendingUp, Clock, CheckCircle, AlertCircle,
  ArrowUpDown, Filter, Trophy, Medal
} from 'lucide-react';

const CHECK_TYPES = [
  { value: 'address_check', label: 'Address Verification' },
  { value: 'employment_check', label: 'Employment Verification' },
  { value: 'education_check', label: 'Education Verification' },
  { value: 'criminal_court_check', label: 'Criminal Court Check' },
  { value: 'court_advocate_check', label: 'Criminal Advocate Check' },
  { value: 'id_check', label: 'ID Verification' },
  { value: 'world_criminal_db', label: 'World Database Check' },
  { value: 'drug_test', label: 'Drug Test' },
  { value: 'credit_check', label: 'Credit/CIBIL Check' },
  { value: 'uan_check', label: 'UAN Verification' },
  { value: 'reference_check', label: 'Reference Check' },
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Chandigarh',
  'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Dadra and Nagar Haveli',
  'Daman and Diu', 'Lakshadweep', 'Andaman and Nicobar Islands'
];

const CITIES_BY_STATE = {
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Amravati', 'Navi Mumbai'],
  'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belgaum', 'Gulbarga', 'Davangere', 'Bellary', 'Shimoga', 'Tumkur'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Tiruppur', 'Vellore', 'Erode', 'Thoothukudi'],
  'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam', 'Ramagundam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet'],
  'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Junagadh', 'Gandhinagar', 'Anand', 'Nadiad'],
  'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Bikaner', 'Ajmer', 'Bhilwara', 'Alwar', 'Sikar', 'Sri Ganganagar'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Ghaziabad', 'Noida', 'Meerut', 'Allahabad', 'Bareilly', 'Aligarh'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman', 'Malda', 'Baharampur', 'Habra', 'Kharagpur'],
  'Madhya Pradesh': ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Ujjain', 'Sagar', 'Dewas', 'Satna', 'Ratlam', 'Rewa'],
  'Delhi': ['New Delhi', 'North Delhi', 'South Delhi', 'East Delhi', 'West Delhi', 'Central Delhi'],
  'Haryana': ['Faridabad', 'Gurgaon', 'Panipat', 'Ambala', 'Yamunanagar', 'Rohtak', 'Hisar', 'Karnal', 'Sonipat', 'Panchkula'],
  'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Mohali', 'Pathankot', 'Hoshiarpur', 'Batala', 'Moga'],
  'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kollam', 'Palakkad', 'Alappuzha', 'Kannur', 'Kottayam', 'Malappuram'],
};

export const VendorPricingComparison = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [selectedCheckType, setSelectedCheckType] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [sortField, setSortField] = useState('final_price');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchComparison = async () => {
    if (!selectedCheckType) {
      toast.error('Please select a check type');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ check_type: selectedCheckType });
      const actualState = selectedState === 'all_states' ? '' : selectedState;
      if (actualState) params.append('state', actualState);
      if (selectedCity) params.append('city', selectedCity);

      const res = await axios.get(`/vendors/pricing/compare?${params.toString()}`);
      setComparisonData(res.data);
    } catch (error) {
      console.error('Failed to fetch comparison:', error);
      toast.error('Failed to load pricing comparison');
    } finally {
      setLoading(false);
    }
  };

  const handleStateChange = (state) => {
    setSelectedState(state);
    setSelectedCity(''); // Reset city when state changes
  };

  const getCitiesForState = (state) => {
    if (!state || state === 'all_states') return [];
    return CITIES_BY_STATE[state] || [];
  };

  const getCheckTypeLabel = (value) => {
    const found = CHECK_TYPES.find(t => t.value === value);
    return found ? found.label : value?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getSortedVendors = () => {
    if (!comparisonData?.vendors) return [];
    
    return [...comparisonData.vendors].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = sortOrder === 'asc' ? Infinity : -Infinity;
      if (bVal === null || bVal === undefined) bVal = sortOrder === 'asc' ? Infinity : -Infinity;
      
      if (sortOrder === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getPriceTypeBadge = (priceType) => {
    switch (priceType) {
      case 'city':
        return <Badge className="bg-purple-100 text-purple-700 text-xs"><Building2 className="w-3 h-3 mr-1" />City</Badge>;
      case 'state':
        return <Badge className="bg-blue-100 text-blue-700 text-xs"><MapPin className="w-3 h-3 mr-1" />State</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600 text-xs">Base</Badge>;
    }
  };

  const getRankBadge = (index) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-slate-400" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-slate-400 text-sm font-medium">#{index + 1}</span>;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-indigo-600" />
            Vendor Pricing Comparison
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filter Controls */}
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-slate-600">Check Type *</Label>
                  <Select value={selectedCheckType} onValueChange={setSelectedCheckType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select check type" />
                    </SelectTrigger>
                    <SelectContent>
                      {CHECK_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">State (Optional)</Label>
                  <Select value={selectedState || 'all_states'} onValueChange={handleStateChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="All states" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_states">All States</SelectItem>
                      {INDIAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">City (Optional)</Label>
                  <Select 
                    value={selectedCity || 'all_cities'} 
                    onValueChange={(v) => setSelectedCity(v === 'all_cities' ? '' : v)}
                    disabled={!selectedState || selectedState === 'all_states'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={selectedState && selectedState !== 'all_states' ? "All cities" : "Select state first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_cities">All Cities</SelectItem>
                      {getCitiesForState(selectedState).map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={fetchComparison} 
                    disabled={!selectedCheckType || loading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Compare Prices
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {comparisonData && (
            <div className="grid grid-cols-5 gap-3">
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-indigo-700">{comparisonData.total_vendors}</div>
                  <div className="text-xs text-indigo-600">Total Vendors</div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{comparisonData.vendors_with_pricing}</div>
                  <div className="text-xs text-green-600">With Pricing</div>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">
                    {comparisonData.lowest_price ? `₹${comparisonData.lowest_price}` : '-'}
                  </div>
                  <div className="text-xs text-emerald-600 flex items-center justify-center gap-1">
                    <TrendingDown className="w-3 h-3" /> Lowest
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">
                    {comparisonData.average_price ? `₹${comparisonData.average_price}` : '-'}
                  </div>
                  <div className="text-xs text-amber-600">Average</div>
                </CardContent>
              </Card>
              <Card className="bg-rose-50 border-rose-200">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-rose-700">
                    {comparisonData.highest_price ? `₹${comparisonData.highest_price}` : '-'}
                  </div>
                  <div className="text-xs text-rose-600 flex items-center justify-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Highest
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Search Context */}
          {comparisonData && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Filter className="w-4 h-4" />
              <span>Showing prices for</span>
              <Badge className="bg-indigo-100 text-indigo-700">{getCheckTypeLabel(comparisonData.check_type)}</Badge>
              {comparisonData.state && (
                <>
                  <span>in</span>
                  <Badge className="bg-blue-100 text-blue-700">{comparisonData.state}</Badge>
                </>
              )}
              {comparisonData.city && (
                <>
                  <span>→</span>
                  <Badge className="bg-purple-100 text-purple-700">{comparisonData.city}</Badge>
                </>
              )}
            </div>
          )}

          {/* Comparison Table */}
          {comparisonData && (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-indigo-50">
                      <TableHead className="w-12">Rank</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Coverage</TableHead>
                      <TableHead 
                        className="text-right cursor-pointer hover:bg-indigo-100"
                        onClick={() => toggleSort('final_price')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Final Price
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Price Type</TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-indigo-100"
                        onClick={() => toggleSort('tat_days')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          TAT
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Performance</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getSortedVendors().length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                          <Scale className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                          <p>No vendors found for this check type</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      getSortedVendors().map((vendor, idx) => (
                        <TableRow 
                          key={vendor.vendor_id} 
                          className={`hover:bg-slate-50 ${idx === 0 && vendor.has_pricing_configured ? 'bg-green-50' : ''}`}
                        >
                          <TableCell className="text-center">
                            {vendor.has_pricing_configured && vendor.final_price > 0 ? getRankBadge(idx) : '-'}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-semibold text-slate-800">{vendor.vendor_name}</p>
                              <p className="text-xs text-slate-500">{vendor.contact_person}</p>
                              <p className="text-xs text-slate-400">{vendor.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {vendor.coverage_type === 'pan_india' ? 'Pan India' : 'Regional'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {vendor.has_pricing_configured && vendor.final_price > 0 ? (
                              <div>
                                <span className="text-lg font-bold text-slate-800">₹{vendor.final_price.toLocaleString()}</span>
                                {vendor.base_price !== vendor.final_price && (
                                  <p className="text-xs text-slate-400">Base: ₹{vendor.base_price}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">Not configured</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {vendor.has_pricing_configured ? getPriceTypeBadge(vendor.price_type) : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-blue-100 text-blue-800">
                              <Clock className="w-3 h-3 mr-1" />
                              {vendor.tat_days}d
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="text-xs">
                              <p>Completed: <span className="font-semibold text-green-600">{vendor.total_completed}</span></p>
                              <p>Assigned: <span className="font-semibold">{vendor.total_assigned}</span></p>
                              {vendor.avg_tat_days > 0 && (
                                <p>Avg TAT: <span className="font-semibold">{vendor.avg_tat_days.toFixed(1)}d</span></p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {vendor.onboarding_status === 'active' ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {vendor.onboarding_status || 'Pending'}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {!comparisonData && !loading && (
            <Card className="bg-slate-50">
              <CardContent className="py-12 text-center">
                <Scale className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Compare Vendor Pricing</h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  Select a check type and optionally a state/city to compare pricing across all vendors. 
                  Find the best value for your verification needs.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VendorPricingComparison;
